import React, { useEffect, useState, useMemo } from "react";
import {
  Card,
  Grid,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Typography,
  Box,
  Divider,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import CloseIcon from "@mui/icons-material/Close";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import LoginIcon from "@mui/icons-material/Login";
import LogoutIcon from "@mui/icons-material/Logout";
import WarningIcon from "@mui/icons-material/Warning";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import DeleteIcon from "@mui/icons-material/Delete";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";

import dayjs from "dayjs";
import axios from "axios";
import { useQuery } from "@tanstack/react-query";
import { compressImage } from "utils/imageCompression";

function Attendance() {
  const navigate = useNavigate();
  const [userId, setUserId] = useState(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showWarningDialog, setShowWarningDialog] = useState(false);
  const [checkedIn, setCheckedIn] = useState(false);
  const [checkedOut, setCheckedOut] = useState(false);
  const [logInTime, setLogInTime] = useState(null);
  const [logOutTime, setLogOutTime] = useState(null);
  const [geotagImg, setGeotagImg] = useState(null);
  const [confirmAction, setConfirmAction] = useState("");
  const [warningMessage, setWarningMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [uploadedImageUrl, setUploadedImageUrl] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [actionTimestamp, setActionTimestamp] = useState(null);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const [currentTime, setCurrentTime] = useState(dayjs());
  
  const hoursToday = useMemo(() => {
    if (!checkedIn || !logInTime) return "--";

    const start = dayjs(logInTime, "HH:mm:ss");
    const end = checkedOut && logOutTime ? dayjs(logOutTime, "HH:mm:ss") : currentTime;

    const diffHours = end.diff(start, "hour");
    const diffMinutes = end.diff(start, "minute") % 60;

    return `${diffHours}h ${diffMinutes}m`;
  }, [checkedIn, checkedOut, logInTime, logOutTime, currentTime]);

  const statusDisplay = useMemo(() => {
    if (checkedOut) return "Completed";
    if (checkedIn) return "Active";
    return "Pending";
  }, [checkedIn, checkedOut]);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(dayjs());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Fetch current user
  const { data: user, isLoading: userLoading } = useQuery({
    queryKey: ["auth-me"],
    queryFn: async () => {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/auth/me`, {}, { withCredentials: true });
      setUserId(response.data.user_id);
      return response.data;
    },
    retry: false,
  });

  // Check today's attendance status
  const checkAttendanceStatus = async (uid) => {
    if (!uid) return;
    // Reset states before checking to ensure a clean state for the current date
    setCheckedIn(false);
    setCheckedOut(false);
    setLogInTime(null);
    setLogOutTime(null);
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/attendance/check`, {
        params: {
          user_id: uid,
          attendance_date: dayjs().format("DD-MM-YYYY"),
        },
        withCredentials: true,
      });

      const info = response.data?.marked;
      setCheckedIn(Boolean(info?.login_done));
      setCheckedOut(Boolean(info?.logout_done));
      setLogInTime(info?.logIn_time || null);
      setLogOutTime(info?.logOut_time || null);
      setGeotagImg(info?.geotag_img || null);

      // if (info?.autoLoggedOut) {
      //   setSnackbar({
      //     open: true,
      //     message: "Previous session was automatically closed at 6:00 PM as no log-out was recorded.",
      //     severity: "warning",
      //   });
      // }
    } catch (error) {
      console.error("Status check failed:", error);
    }
  };

  useEffect(() => {
    if (userId) checkAttendanceStatus(userId);
  }, [userId]);

  const formatTime = (timeString) => {
    if (!timeString) return "-";
    const [hours, minutes] = timeString.split(":");
    let h = parseInt(hours);
    const ampm = h >= 12 ? "PM" : "AM";
    h = h % 12;
    h = h ? h : 12;
    return `${h}:${minutes} ${ampm}`;
  };

  const handleImageChange = async (event) => {
    const file = event.target.files[0];
    if (file) {
      const maxSize = 25 * 1024 * 1024; // 25MB
      if (file.size > maxSize) {
        setSnackbar({
          open: true,
          message: "File size exceeds 25MB limit. Please upload a smaller image.",
          severity: "error",
        });
        event.target.value = ""; // Clear input
        return;
      }
      setSelectedFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewUrl(reader.result);
      };
      reader.readAsDataURL(file);

      // Upload immediately to vercel blob
      setIsUploading(true);
      try {
        const compressedFile = await compressImage(file);
        // console.log(`DEBUG - Attendance Upload: Original Size: ${(file.size / (1024*1024)).toFixed(2)} MB, Compressed Size: ${(compressedFile.size / (1024*1024)).toFixed(2)} MB`);
        const formData = new FormData();
        formData.append("file", compressedFile);
        const response = await axios.post(`${process.env.REACT_APP_API_URL}/reports/upload`, formData, {
          withCredentials: true,
          headers: { "Content-Type": "multipart/form-data" },
        });
        if (response.data && response.data.url) {
          setUploadedImageUrl(response.data.url);
        }
      } catch (error) {
        console.error("Image upload failed:", error);
        setSnackbar({
          open: true,
          message: "Image upload failed. Please try again.",
          severity: "error",
        });
        setSelectedFile(null);
        setPreviewUrl(null);
      } finally {
        setIsUploading(false);
      }
    }
  };

  const handleActionClick = (action) => {
    if (action === "check-in") {
      if (checkedOut) {
        setWarningMessage("You have already checked out for today. Attendance is already closed for this session.");
        setShowWarningDialog(true);
      } else if (checkedIn) {
        setWarningMessage("You have already checked in for today. Duplicated check-ins are not permitted.");
        setShowWarningDialog(true);
      } else {
        setSelectedFile(null);
        setPreviewUrl(null);
        setUploadedImageUrl(null);
        setConfirmAction("check-in");
        setActionTimestamp(dayjs().format("HH:mm:ss"));
        setShowConfirmDialog(true);
      }
    } else if (action === "check-out") {
      if (!checkedIn) {
        setWarningMessage("You must check in first before you can check out.");
        setShowWarningDialog(true);
      } else if (checkedOut) {
        setWarningMessage("You have already checked out for today. Attendance for the day is complete.");
        setShowWarningDialog(true);
      } else {
        setConfirmAction("check-out");
        setActionTimestamp(dayjs().format("HH:mm:ss"));
        setShowConfirmDialog(true);
      }
    }
  };

  const handleConfirmAction = async () => {
    setIsSubmitting(true);
    try {
      const isCheckIn = confirmAction === "check-in";
      const url = isCheckIn
        ? `${process.env.REACT_APP_API_URL}/attendance/mark`
        : `${process.env.REACT_APP_API_URL}/attendance/logout`;

      const payload = {
        user_id: userId,
        attendance_date: dayjs().format("DD-MM-YYYY"),
      };

      if (isCheckIn) {
        payload.status = "present";
        payload.logIn_time = dayjs().format("HH:mm:ss");
        if (uploadedImageUrl) {
          payload.geotag_img = uploadedImageUrl;
        }
      } else {
        payload.logOut_time = dayjs().format("HH:mm:ss");
      }

      await axios[isCheckIn ? "post" : "put"](url, payload, {
        withCredentials: true,
      });

      setSnackbar({
        open: true,
        message: isCheckIn ? "Check-in successful! Have a productive day." : "Check-out successful! Goodbye.",
        severity: "success",
      });

      setShowConfirmDialog(false);
      checkAttendanceStatus(userId);
    } catch (error) {
      console.log(error);
      setSnackbar({
        open: true,
        message: "Action failed. Please try again or contact support.",
        severity: "error",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (userLoading)
    return (
      <MDBox display="flex" justifyContent="center" alignItems="center" height="100vh">
        <CircularProgress color="info" />
      </MDBox>
    );

  // Role check
  if (user?.role_id === 1 || user?.role_id === 2) {
    return (
      <DashboardLayout>
        <MDBox mt={4} display="flex" justifyContent="center">
          <Card
            sx={{
              p: 4,
              maxWidth: 600,
              textAlign: "center",
              borderRadius: "15px",
              boxShadow: "0 10px 30px rgba(0,0,0,0.1)",
            }}
          >
            <WarningIcon color="error" sx={{ fontSize: 60, mb: 2 }} />
            <MDTypography variant="h4" fontWeight="bold">
              Access Restricted
            </MDTypography>
            <MDTypography variant="body2" color="text" mt={1}>
              Administrative accounts are not required to log daily attendance.
            </MDTypography>
          </Card>
        </MDBox>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <MDBox py={3} sx={{ maxWidth: "1200px", mx: "auto" }}>
        {/* Header Card */}
        <Card
          sx={{
            background: "linear-gradient(135deg, #0a7159 0%, #063d31 100%)",
            borderRadius: "24px",
            p: { xs: 3, md: 5 },
            mb: 4,
            position: "relative",
            overflow: "hidden",
            boxShadow: "0 20px 60px rgba(10, 113, 89, 0.3)",
            border: "none",
            "&::before": {
              content: '""',
              position: "absolute",
              top: "-100px",
              right: "-100px",
              width: "300px",
              height: "300px",
              background: "radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)",
              borderRadius: "50%",
            },
            "&::after": {
              content: '""',
              position: "absolute",
              bottom: "-50px",
              left: "-50px",
              width: "200px",
              height: "200px",
              background: "radial-gradient(circle, rgba(255,255,255,0.08) 0%, transparent 70%)",
              borderRadius: "50%",
            },
          }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            flexWrap="wrap"
            gap={4}
            position="relative"
            zIndex={1}
          >
            <Box flex={1} minWidth={{ xs: "100%", md: "300px" }}>
              <MDTypography
                variant="h2"
                color="white"
                fontWeight="bold"
                sx={{
                  fontSize: { xs: "24px", md: "32px" },
                  mb: 1,
                  letterSpacing: "-0.5px",
                }}
              >
                {currentTime.hour() < 12 ? "Good Morning" : currentTime.hour() < 17 ? "Good Afternoon" : "Good Evening"}, {user?.user_name}! 👋
              </MDTypography>
              <MDTypography
                variant="body1"
                color="white"
                sx={{
                  opacity: 0.85,
                  fontSize: "15px",
                  lineHeight: 1.5,
                  fontWeight: 400,
                }}
              >
                Welcome back to the CMGFP Attendance Portal. Track your daily logs with ease.
              </MDTypography>
              <MDButton
                variant="outlined"
                color="white"
                size="small"
                onClick={() => navigate("/attendance-list")}
                startIcon={<EventAvailableIcon />}
                sx={{
                  mt: 2,
                  borderColor: "rgba(255, 255, 255, 0.4)",
                  color: "white",
                  borderRadius: "10px",
                  textTransform: "none",
                  fontSize: "13px",
                  padding: "8px 20px",
                  "&:hover": {
                    borderColor: "white",
                    backgroundColor: "rgba(255, 255, 255, 0.1)",
                  },
                }}
              >
                View Attendance Logs
              </MDButton>
            </Box>
            <Box
              sx={{
                background: "rgba(255, 255, 255, 0.15)",
                backdropFilter: "blur(20px)",
                border: "1.5px solid rgba(255, 255, 255, 0.25)",
                borderRadius: "20px",
                p: "24px 32px",
                textAlign: "center",
                minWidth: { xs: "100%", sm: "240px" },
              }}
            >
              <MDTypography
                variant="h4"
                color="white"
                fontWeight="bold"
                sx={{
                  textTransform: "uppercase",
                  letterSpacing: "2px",
                  mb: 0.5,
                  fontSize: "24px",
                }}
              >
                {currentTime.format("dddd")}
              </MDTypography>
              <MDTypography
                variant="h6"
                color="white"
                sx={{ opacity: 0.9, fontWeight: 500, fontSize: "16px" }}
              >
                {currentTime.format("MMMM D, YYYY")}
              </MDTypography>
            </Box>
          </Box>
        </Card>

        {geotagImg === "On Duty" && (
          <Card
            sx={{
              background: "linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)",
              borderRadius: "24px",
              p: 3,
              mb: 4,
              boxShadow: "0 10px 30px rgba(30, 58, 138, 0.2)",
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              alignItems: "center",
              gap: 3,
              border: "none",
            }}
          >
            <Box sx={{ fontSize: "40px" }}>💼</Box>
            <Box>
              <MDTypography variant="h4" color="white" fontWeight="bold">
                On Duty Automatically Marked Present
              </MDTypography>
              <MDTypography variant="body2" color="white" sx={{ opacity: 0.9, mt: 0.5, fontSize: "14px" }}>
                Your approved leave request for <b>On Duty</b> has automatically marked today&apos;s attendance as <b>Present</b> (12:00 AM - 11:59 PM). The manual check-in/out buttons below have been disabled.
              </MDTypography>
            </Box>
          </Card>
        )}

        {/* Main Grid */}
        <Grid container spacing={4}>
          {/* Attendance Actions Card */}
          <Grid item xs={12} md={6}>
            <Card
              sx={{
                borderRadius: "24px",
                p: 4,
                boxShadow: "0 8px 32px rgba(0, 0, 0, 0.08)",
                height: "100%",
                width: '575px',
                transition: "transform 0.3s ease, box-shadow 0.3s ease",
                "&:hover": {
                  transform: "translateY(-4px)",
                  boxShadow: "0 12px 48px rgba(0, 0, 0, 0.12)",
                },
              }}
            >
              <Box display="flex" alignItems="center" gap={2} mb={3.5}>
                <Box
                  sx={{
                    width: "56px",
                    height: "56px",
                    borderRadius: "16px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "28px",
                    background: "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)",
                    boxShadow: "0 8px 24px rgba(0, 0, 0, 0.12)",
                    color: "white",
                  }}
                >
                  <CheckCircleIcon fontSize="inherit" />
                </Box>
                <MDTypography variant="h4" fontWeight="bold" sx={{ color: "#1e293b", fontSize: "22px", letterSpacing: "-0.3px" }}>
                  Attendance Actions
                </MDTypography>
              </Box>

              {/* Quick Stats */}
              <Box display="grid" gridTemplateColumns="repeat(2, 1fr)" gap={1.5} mb={2.5}>
                <Box sx={{ p: 1.5, background: "#f8fafc", borderRadius: "10px", textAlign: "center" }}>
                  <MDTypography variant="h5" fontWeight="bold" sx={{ color: "#0a7159", fontSize: "18px" }}>
                    {hoursToday}
                  </MDTypography>
                  <MDTypography variant="caption" sx={{ color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.5px", fontSize: "11px", mt: 0.5, display: "block" }}>
                    Hours Today
                  </MDTypography>
                </Box>
                <Box sx={{ p: 1.5, background: "#f8fafc", borderRadius: "10px", textAlign: "center" }}>
                  <MDTypography variant="h5" fontWeight="bold" sx={{ color: "#0a7159", fontSize: "18px" }}>
                    {statusDisplay}
                  </MDTypography>
                  <MDTypography variant="caption" sx={{ color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.5px", fontSize: "11px", mt: 0.5, display: "block" }}>
                    Status
                  </MDTypography>
                </Box>
              </Box>

              {/* Status Section */}
              <Box display="flex" flexDirection="column" gap={2} mb={3.5}>
                <Box
                  onClick={() => !checkedIn && !checkedOut && handleActionClick("check-in")}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    p: "20px 24px",
                    borderRadius: "16px",
                    border: "2px solid",
                    cursor: (checkedIn || checkedOut) ? "not-allowed" : "pointer",
                    transition: "all 0.3s ease",
                    background: checkedIn
                      ? "linear-gradient(135deg, rgba(34, 197, 94, 0.08) 0%, rgba(34, 197, 94, 0.04) 100%)"
                      : "transparent",
                    borderColor: checkedIn ? "rgba(34, 197, 94, 0.3)" : "#e2e8f0",
                    opacity: (checkedIn || checkedOut) && confirmAction !== "check-in" ? 0.7 : 1,
                    "&:hover": {
                      borderColor: (checkedIn || checkedOut) ? "" : "#3b82f6",
                      background: (checkedIn || checkedOut) ? "" : "rgba(59, 130, 246, 0.05)",
                    }
                  }}
                >
                  <Box display="flex" alignItems="center" gap={1.5}>
                    <MDTypography sx={{ fontSize: "24px" }}>{checkedIn ? "🟢" : "⚪"}</MDTypography>
                    <MDTypography sx={{ fontSize: "16px", fontWeight: 600, color: "#334155" }}>
                      Daily Check-In
                    </MDTypography>
                  </Box>
                  {checkedIn && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.7, p: "6px 14px", borderRadius: "20px", background: "rgba(34, 197, 94, 0.15)", color: "#15803d", fontSize: "13px", fontWeight: 600 }}>
                      <CheckCircleIcon sx={{ fontSize: "16px" }} />
                      Completed
                    </Box>
                  )}
                </Box>

                <Box
                  onClick={() => checkedIn && !checkedOut && handleActionClick("check-out")}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    p: "20px 24px",
                    borderRadius: "16px",
                    border: "2px solid",
                    cursor: (!checkedIn || checkedOut) ? "not-allowed" : "pointer",
                    transition: "all 0.3s ease",
                    background: checkedOut
                      ? "linear-gradient(135deg, rgba(239, 68, 68, 0.08) 0%, rgba(239, 68, 68, 0.04) 100%)"
                      : "transparent",
                    borderColor: checkedOut ? "rgba(239, 68, 68, 0.3)" : "#e2e8f0",
                    opacity: (!checkedIn || checkedOut) && confirmAction !== "check-out" ? 0.7 : 1,
                    "&:hover": {
                      borderColor: (!checkedIn || checkedOut) ? "" : "#ef4444",
                      background: (!checkedIn || checkedOut) ? "" : "rgba(239, 68, 68, 0.05)",
                    }
                  }}
                >
                  <Box display="flex" alignItems="center" gap={1.5}>
                    <MDTypography sx={{ fontSize: "24px" }}>{checkedOut ? "🔴" : "⚪"}</MDTypography>
                    <MDTypography sx={{ fontSize: "16px", fontWeight: 600, color: "#334155" }}>
                      Daily Check-Out
                    </MDTypography>
                  </Box>
                  {checkedOut && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.7, p: "6px 14px", borderRadius: "20px", background: "rgba(239, 68, 68, 0.15)", color: "#b91c1c", fontSize: "13px", fontWeight: 600 }}>
                      <CheckCircleIcon sx={{ fontSize: "16px" }} />
                      Completed
                    </Box>
                  )}
                </Box>
              </Box>

              <Divider sx={{ height: "1px", background: "linear-gradient(90deg, transparent 0%, #e2e8f0 50%, transparent 100%)", my: 3 }} />

              {/* Timestamps Section */}
              <Box>
                <MDTypography sx={{ fontSize: "11px", fontWeight: 700, color: "#94a3b8", letterSpacing: "1.5px", textTransform: "uppercase", mb: 2 }}>
                  Verified Timestamps
                </MDTypography>
                <Box display="grid" gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr" }} gap={2}>
                  <Box sx={{
                    p: 2.5,
                    borderRadius: "14px",
                    border: "2px solid",
                    borderColor: checkedIn ? "rgba(34, 197, 94, 0.3)" : "#e2e8f0",
                    background: checkedIn ? "linear-gradient(135deg, rgba(34, 197, 94, 0.06) 0%, rgba(34, 197, 94, 0.02) 100%)" : "transparent",
                    display: "flex",
                    alignItems: "center",
                    gap: 1.75
                  }}>
                    <MDTypography sx={{ fontSize: "32px" }}>🕐</MDTypography>
                    <Box>
                      <MDTypography sx={{ fontSize: "11px", fontWeight: 700, color: "#94a3b8", letterSpacing: "1px", mb: 0.5 }}>CHECK-IN</MDTypography>
                      <MDTypography sx={{ fontSize: "20px", fontWeight: 700, color: checkedIn ? "#15803d" : "#1e293b" }}>
                        {checkedIn ? formatTime(logInTime) : "Pending"}
                      </MDTypography>
                    </Box>
                  </Box>
                  <Box sx={{
                    p: 2.5,
                    borderRadius: "14px",
                    border: "2px solid",
                    borderColor: checkedOut ? "rgba(239, 68, 68, 0.3)" : "#e2e8f0",
                    background: checkedOut ? "linear-gradient(135deg, rgba(239, 68, 68, 0.06) 0%, rgba(239, 68, 68, 0.02) 100%)" : "transparent",
                    display: "flex",
                    alignItems: "center",
                    gap: 1.75
                  }}>
                    <MDTypography sx={{ fontSize: "32px" }}>🕐</MDTypography>
                    <Box>
                      <MDTypography sx={{ fontSize: "11px", fontWeight: 700, color: "#94a3b8", letterSpacing: "1px", mb: 0.5 }}>CHECK-OUT</MDTypography>
                      <MDTypography sx={{ fontSize: "20px", fontWeight: 700, color: checkedOut ? "#b91c1c" : "#1e293b" }}>
                        {checkedOut ? formatTime(logOutTime) : "Pending"}
                      </MDTypography>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Card>
          </Grid>

          {/* Guidelines Card */}
          <Grid item xs={12} md={6}>
            <Card
              sx={{
                borderRadius: "24px",
                p: 4,
                boxShadow: "0 8px 32px rgba(0, 0, 0, 0.08)",
                height: "100%",
                width: '575px',

              }}
            >
              <Box display="flex" alignItems="center" gap={2} mb={3.5}>
                <Box
                  sx={{
                    width: "56px",
                    height: "56px",
                    borderRadius: "16px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "28px",
                    background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                    boxShadow: "0 8px 24px rgba(0, 0, 0, 0.12)",
                    color: "white",
                  }}
                >
                  <WarningIcon fontSize="inherit" />
                </Box>
                <MDTypography variant="h4" fontWeight="bold" sx={{ color: "#1e293b", fontSize: "22px", letterSpacing: "-0.3px" }}>
                  Attendance Guidelines
                </MDTypography>
              </Box>

              <Box component="ul" sx={{ listStyle: "none", p: 0, m: 0, display: "flex", flexDirection: "column", gap: 2 }}>
                {[
                  "Attendance can only be marked once per calendar day.",
                  "Check-in is required at the start of your field duty.",
                  "Ensure you check out before leaving the portal at the end of the day.",
                  "Contact support immediately if you experience technical difficulties."
                ].map((text, index) => (
                  <Box
                    key={index}
                    component="li"
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 1.5,
                      p: 2,
                      background: "#f8fafc",
                      borderRadius: "12px",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        background: "#f1f5f9",
                        transform: "translateX(4px)",
                      }
                    }}
                  >
                    <Box sx={{ width: "8px", height: "8px", background: "#0a7159", borderRadius: "50%", mt: 1, flexShrink: 0 }} />
                    <MDTypography sx={{ fontSize: "14px", color: "#475569", lineHeight: 1.6 }}>
                      {text}
                    </MDTypography>
                  </Box>
                ))}
              </Box>

              {/* Help Section */}
              <Box
                onClick={() => navigate("/ticket-add")}
                sx={{
                  mt: 3,
                  p: 3,
                  background: "linear-gradient(135deg, rgba(59, 130, 246, 0.05) 0%, rgba(37, 99, 235, 0.02) 100%)",
                  border: "2px dashed rgba(59, 130, 246, 0.3)",
                  borderRadius: "16px",
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    background: "linear-gradient(135deg, rgba(59, 130, 246, 0.08) 0%, rgba(37, 99, 235, 0.04) 100%)",
                    borderColor: "rgba(59, 130, 246, 0.5)",
                    transform: "translateY(-2px)",
                    boxShadow: "0 8px 24px rgba(59, 130, 246, 0.15)",
                  }
                }}
              >
                <Box display="flex" alignItems="center" gap={1.25} mb={1}>
                  <MDTypography sx={{ fontSize: "20px" }}>💡</MDTypography>
                  <MDTypography sx={{ fontSize: "12px", fontWeight: 700, color: "#2563eb", letterSpacing: "1.2px" }}>
                    NEED ASSISTANCE?
                  </MDTypography>
                </Box>
                <MDTypography sx={{ fontSize: "14px", color: "#475569", lineHeight: 1.5 }}>
                  Please raise a support ticket immediately.
                </MDTypography>
              </Box>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      {/* Action Confirmation Dialog */}
      <Dialog
        open={showConfirmDialog}
        onClose={() => !isSubmitting && setShowConfirmDialog(false)}
        PaperProps={{ sx: { borderRadius: "16px", p: 1, minWidth: "380px" } }}
      >
        <DialogTitle sx={{ textAlign: "center", pb: 1 }}>
          <Box
            sx={{
              width: "64px",
              height: "64px",
              borderRadius: "50%",
              backgroundColor:
                confirmAction === "check-in" ? "rgba(76, 175, 80, 0.1)" : "rgba(244, 67, 54, 0.1)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto",
              mb: 2,
            }}
          >
            {confirmAction === "check-in" ? (
              <LoginIcon color="success" sx={{ fontSize: "2rem" }} />
            ) : (
              <LogoutIcon color="error" sx={{ fontSize: "2rem" }} />
            )}
          </Box>
          <MDTypography variant="h5" fontWeight="bold">
            Confirm {confirmAction === "check-in" ? "Check In" : "Check Out"}
          </MDTypography>
        </DialogTitle>
        <DialogContent sx={{ textAlign: "center", pt: 2 }}>
          {confirmAction === "check-in" && (
            <Box sx={{ mb: 3 }}>
              <MDTypography
                variant="button"
                fontWeight="bold"
                color="text"
                sx={{ mb: 1, display: "block", textAlign: "left" }}
              >
                Upload Geo-Tagged Image *
              </MDTypography>
              <input
                accept="image/*"
                style={{ display: "none" }}
                id="attendance-image-upload"
                type="file"
                onChange={handleImageChange}
              />
              <label htmlFor="attendance-image-upload">
                {!previewUrl ? (
                  <MDBox
                    sx={{
                      width: "100%",
                      height: "180px",
                      border: "2px dashed #e2e8f0",
                      borderRadius: "16px",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        borderColor: "#0a7159",
                        backgroundColor: "rgba(10, 113, 89, 0.05)",
                        transform: "translateY(-2px)",
                      },
                    }}
                  >
                    {isUploading ? (
                      <MDBox display="flex" flexDirection="column" alignItems="center">
                        <CircularProgress size={30} color="info" />
                        <MDTypography variant="caption" color="text" sx={{ mt: 1 }}>
                          Uploading...
                        </MDTypography>
                      </MDBox>
                    ) : (
                      <>
                        <Box
                          sx={{
                            width: "50px",
                            height: "50px",
                            borderRadius: "50%",
                            backgroundColor: "rgba(10, 113, 89, 0.1)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            mb: 1.5,
                          }}
                        >
                          <PhotoCameraIcon sx={{ fontSize: 28, color: "#0a7159" }} />
                        </Box>
                        <MDTypography variant="button" fontWeight="medium" color="text">
                          Click to upload photo
                        </MDTypography>
                        <MDTypography variant="caption" color="secondary" sx={{ mt: 0.5 }}>
                          Mandatory for secure check-in (Max 25MB)
                        </MDTypography>
                      </>
                    )}
                  </MDBox>
                ) : (
                  <Box sx={{ position: "relative", width: "100%", borderRadius: "16px", overflow: "hidden" }}>
                    <img
                      src={previewUrl}
                      alt="Geo-tagged preview"
                      style={{
                        width: "100%",
                        height: "200px",
                        objectFit: "cover",
                        display: "block",
                        borderRadius: "16px",
                      }}
                    />
                    <Box
                      sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: "linear-gradient(to bottom, rgba(0,0,0,0.3) 0%, transparent 40%)",
                        pointerEvents: "none",
                      }}
                    />
                    <IconButton
                      size="small"
                      sx={{
                        position: "absolute",
                        top: 10,
                        right: 10,
                        backgroundColor: "rgba(255, 255, 255, 0.9)",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                        "&:hover": { backgroundColor: "white", transform: "scale(1.1)" },
                        transition: "all 0.2s ease",
                      }}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setSelectedFile(null);
                        setPreviewUrl(null);
                      }}
                    >
                      <DeleteIcon color="error" sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Box>
                )}
              </label>
            </Box>
          )}
          <MDTypography variant="body2" color="text" sx={{ mb: 2 }}>
            Are you sure you want to {confirmAction} for today? This action will record your current server time as
            the official log.
          </MDTypography>
          <Box sx={{ mt: 2, p: 2, borderRadius: "10px", backgroundColor: "#f8f9fa" }}>
            <MDTypography variant="h5" fontWeight="bold" color="dark">
              {actionTimestamp}
            </MDTypography>
          </Box>
          <MDBox mt={2.5}>
            <MDTypography
              variant="caption"
              color="error"
              fontWeight="bold"
              sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5 }}
            >
              <WarningIcon fontSize="small" /> Note: This action can only be performed once per day.
            </MDTypography>
          </MDBox>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", gap: 2, pb: 2.5, px: 3 }}>
          <MDButton
            variant="outlined"
            color="secondary"
            onClick={() => setShowConfirmDialog(false)}
            disabled={isSubmitting}
            sx={{ minWidth: "110px" }}
          >
            Cancel
          </MDButton>
          <MDButton
            variant="gradient"
            color={confirmAction === "check-in" ? "success" : "error"}
            onClick={handleConfirmAction}
            disabled={isSubmitting || isUploading || (confirmAction === "check-in" && !selectedFile)}
            startIcon={isSubmitting && <CircularProgress size={20} color="inherit" />}
            sx={{ minWidth: "150px" }}
          >
            {isSubmitting ? "Processing..." : "Confirm Action"}
          </MDButton>
        </DialogActions>
      </Dialog >

      {/* Warning Dialog */}
      < Dialog
        open={showWarningDialog}
        onClose={() => setShowWarningDialog(false)
        }
        PaperProps={{ sx: { borderRadius: "16px", p: 1, minWidth: "380px" } }}
      >
        <DialogTitle sx={{ textAlign: "center", pb: 1 }}>
          <Box
            sx={{
              width: "64px",
              height: "64px",
              borderRadius: "50%",
              backgroundColor: "rgba(255, 152, 0, 0.1)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto",
              mb: 2,
            }}
          >
            <WarningIcon color="warning" sx={{ fontSize: "2rem" }} />
          </Box>
          <MDTypography variant="h5" fontWeight="bold">
            Policy Restriction
          </MDTypography>
        </DialogTitle>
        <DialogContent sx={{ textAlign: "center", pt: 2 }}>
          <MDTypography variant="body2" color="text">
            {warningMessage}
          </MDTypography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", pb: 2.5 }}>
          <MDButton variant="gradient" color="warning" onClick={() => setShowWarningDialog(false)}>
            I Understand
          </MDButton>
        </DialogActions>
      </Dialog >

      {/* Snackbar */}
      < Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert severity={snackbar.severity} variant="filled" sx={{ width: "100%", borderRadius: "10px" }}>
          {snackbar.message}
        </Alert>
      </Snackbar >
    </DashboardLayout >
  );
}

export default Attendance;